import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { doc, getDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth, db } from '../config/firebase';
import { isSuperAdmin } from '../utils/roleManager';
import { ArrowLeftIcon, MicrophoneIcon, AdjustmentsHorizontalIcon } from '@heroicons/react/24/outline';

const PROFILE_FIELDS = [
  { key: 'firstName', label: 'First Name' },
  { key: 'middleName', label: 'Middle Name' },
  { key: 'lastName', label: 'Last Name' },
  { key: 'displayName', label: 'Display Name' },
  { key: 'familyRelation', label: 'Family Relation' },
];

const formatDate = (dateValue) => {
  if (!dateValue) return 'N/A';
  try {
    if (dateValue.toDate) return dateValue.toDate().toLocaleDateString();
    return new Date(dateValue).toLocaleDateString();
  } catch {
    return 'N/A';
  }
};

async function callAdminFunction(name, data) {
  const { getFunctions, httpsCallable } = await import('firebase/functions');
  const { getApp } = await import('firebase/app');
  const fns = getFunctions(getApp());
  const fn = httpsCallable(fns, name);
  return fn(data);
}

function SuperAdminUserProfile() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { userId } = useParams();
  const userIsSuperAdmin = isSuperAdmin(currentUser);

  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [families, setFamilies] = useState([]);
  const [recordingCount, setRecordingCount] = useState(null);

  const [fields, setFields] = useState({});
  const [bio, setBio] = useState('');
  const [emailInput, setEmailInput] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);
  const [togglingBeta, setTogglingBeta] = useState(false);
  const [togglingAdmin, setTogglingAdmin] = useState(false);
  const [message, setMessage] = useState(null); // { type: 'success'|'error', text }

  const loadUser = useCallback(async () => {
    setLoading(true);
    try {
      const snap = await getDoc(doc(db, 'users', userId));
      if (!snap.exists()) {
        setUser(null);
        return;
      }
      const data = { id: snap.id, ...snap.data() };
      setUser(data);
      setFields({
        firstName: data.firstName || '',
        middleName: data.middleName || '',
        lastName: data.lastName || '',
        displayName: data.displayName || '',
        familyRelation: data.familyRelation || '',
      });
      setBio(data.bio || '');
      setEmailInput(data.email || '');

      const familyIds = data.familyIds || [];
      if (familyIds.length > 0) {
        const familyDocs = await Promise.all(
          familyIds.map((fid) => getDoc(doc(db, 'families', fid)))
        );
        setFamilies(familyDocs.filter((d) => d.exists()).map((d) => ({ id: d.id, ...d.data() })));
      } else {
        setFamilies([]);
      }

      const recQuery = query(collection(db, 'recordings'), where('userId', '==', userId));
      const recSnap = await getDocs(recQuery);
      setRecordingCount(recSnap.size);
    } catch (error) {
      console.error('Error loading user:', error);
      setMessage({ type: 'error', text: 'Failed to load user.' });
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (!userIsSuperAdmin) {
      navigate('/');
      return;
    }
    loadUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userIsSuperAdmin, navigate, userId]);

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    setMessage(null);
    try {
      await callAdminFunction('adminUpdateUserProfile', {
        userId,
        updates: { ...fields, bio },
      });
      setMessage({ type: 'success', text: 'Profile saved.' });
      await loadUser();
    } catch (error) {
      console.error('Error saving profile:', error);
      setMessage({ type: 'error', text: `Failed to save profile: ${error.message}` });
    } finally {
      setSavingProfile(false);
    }
  };

  const handleSaveEmail = async () => {
    const trimmed = emailInput.trim();
    if (!trimmed || trimmed === user?.email) return;
    if (!window.confirm(`Change this user's email to ${trimmed}? A verification link will be sent to the new address. Nothing else about the account changes.`)) {
      return;
    }
    setSavingEmail(true);
    setMessage(null);
    try {
      await callAdminFunction('adminUpdateUserEmail', { userId, newEmail: trimmed });
      setMessage({ type: 'success', text: 'Email updated — a verification link was sent to the new address.' });
      await loadUser();
    } catch (error) {
      console.error('Error updating email:', error);
      setMessage({ type: 'error', text: `Failed to update email: ${error.message}` });
    } finally {
      setSavingEmail(false);
    }
  };

  const handleBetaToggle = async () => {
    const newStatus = !user.isBeta;
    setTogglingBeta(true);
    setMessage(null);
    try {
      await updateDoc(doc(db, 'users', userId), {
        isBeta: newStatus,
        updatedAt: new Date(),
        updatedBy: currentUser.uid,
      });
      try {
        await callAdminFunction(newStatus ? 'grantFriendsAccess' : 'revokeFriendsAccess', { userId });
      } catch (stripeError) {
        console.error('Error syncing Friends Access subscription in Stripe:', stripeError);
        setMessage({ type: 'error', text: 'Friends Access was granted, but the Stripe subscription sync failed — the account still has full access.' });
      }
      await loadUser();
    } catch (error) {
      console.error('Error updating beta status:', error);
      setMessage({ type: 'error', text: 'Failed to update Friends Access status.' });
    } finally {
      setTogglingBeta(false);
    }
  };

  const handleSuperAdminToggle = async () => {
    if (userId === currentUser.uid) return;
    setTogglingAdmin(true);
    setMessage(null);
    try {
      await updateDoc(doc(db, 'users', userId), {
        superAdmin: !user.superAdmin,
        updatedAt: new Date(),
        updatedBy: currentUser.uid,
      });
      await loadUser();
    } catch (error) {
      console.error('Error updating super admin status:', error);
      setMessage({ type: 'error', text: 'Failed to update super admin status.' });
    } finally {
      setTogglingAdmin(false);
    }
  };

  const handleResetPassword = async () => {
    try {
      await sendPasswordResetEmail(auth, user.email);
      setMessage({ type: 'success', text: `Password reset email sent to ${user.email}` });
    } catch (error) {
      console.error('Error sending password reset email:', error);
      setMessage({ type: 'error', text: `Failed to send password reset email: ${error.message}` });
    }
  };

  if (!userIsSuperAdmin) return null;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cerulean" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12 text-center">
        <p className="text-gray-500">User not found.</p>
        <Link to="/super-admin" className="text-cerulean font-medium mt-4 inline-block">Back to Super Admin</Link>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
      <button
        onClick={() => navigate('/super-admin')}
        className="text-cerulean hover:text-cerulean/80 text-sm font-medium flex items-center gap-1"
      >
        <ArrowLeftIcon className="w-4 h-4" />
        Back to Super Admin
      </button>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {user.displayName || 'Unnamed User'}
          </h1>
          <p className="text-sm text-gray-500">{user.email}</p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to={`/super-admin/users/${userId}/recordings`}
            className="px-4 py-2 bg-cerulean text-white rounded-lg font-semibold text-sm flex items-center gap-2 hover:bg-cerulean/90 transition-colors"
          >
            <MicrophoneIcon className="w-4 h-4" />
            Recordings {recordingCount !== null ? `(${recordingCount})` : ''}
          </Link>
          <Link
            to={`/super-admin/users/${userId}/audio-enhancement`}
            className="px-4 py-2 bg-cerulean/10 text-cerulean rounded-lg font-semibold text-sm flex items-center gap-2 hover:bg-cerulean/20 transition-colors"
          >
            <AdjustmentsHorizontalIcon className="w-4 h-4" />
            Audio Enhancement
          </Link>
        </div>
      </div>

      {message && (
        <div className={`rounded-xl p-4 text-sm ${
          message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
        }`}>
          {message.text}
        </div>
      )}

      {/* Status row */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Status</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div>
            <div className="text-xs text-gray-400 uppercase font-medium mb-1">Super Admin</div>
            <div className="flex items-center gap-2">
              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                user.superAdmin ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
              }`}>
                {user.superAdmin ? 'Yes' : 'No'}
              </span>
              <button
                onClick={handleSuperAdminToggle}
                disabled={togglingAdmin || userId === currentUser.uid}
                title={userId === currentUser.uid ? 'Cannot modify your own status' : ''}
                className={`text-xs px-2 py-1 rounded disabled:opacity-40 ${
                  user.superAdmin ? 'bg-red-100 text-red-700 hover:bg-red-200' : 'bg-green-100 text-green-700 hover:bg-green-200'
                }`}
              >
                {user.superAdmin ? 'Remove' : 'Grant'}
              </button>
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-400 uppercase font-medium mb-1">Friends Access</div>
            <div className="flex items-center gap-2">
              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                user.isBeta ? 'bg-cerulean/15 text-cerulean' : 'bg-gray-100 text-gray-500'
              }`}>
                {user.isBeta ? 'Friends' : 'No'}
              </span>
              <button
                onClick={handleBetaToggle}
                disabled={togglingBeta}
                className={`text-xs px-2 py-1 rounded disabled:opacity-40 ${
                  user.isBeta ? 'bg-red-100 text-red-700 hover:bg-red-200' : 'bg-cerulean/10 text-cerulean hover:bg-cerulean/20'
                }`}
              >
                {togglingBeta ? '…' : user.isBeta ? 'Revoke' : 'Grant'}
              </button>
            </div>
          </div>
          <div>
            <div className="text-xs text-gray-400 uppercase font-medium mb-1">Plan</div>
            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
              user.subscriptionPlan === 'legacy' ? 'bg-cerulean/15 text-cerulean' :
              user.subscriptionPlan === 'plus' ? 'bg-aqua-deep/15 text-aqua-deep' :
              'bg-gray-100 text-gray-500'
            }`}>
              {user.subscriptionPlan === 'legacy' ? 'Family Legacy' :
               user.subscriptionPlan === 'plus' ? 'Family+' : 'Free'}
            </span>
          </div>
          <div>
            <div className="text-xs text-gray-400 uppercase font-medium mb-1">Joined</div>
            <span className="text-sm text-gray-700">{formatDate(user.createdAt)}</span>
          </div>
        </div>
        <div className="mt-4 pt-4 border-t border-gray-100">
          <button
            onClick={handleResetPassword}
            className="text-xs px-3 py-1.5 rounded bg-blue-100 text-blue-700 hover:bg-blue-200 font-medium"
          >
            Send Password Reset Email
          </button>
        </div>
      </div>

      {/* Email */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Email Address</h2>
        <p className="text-xs text-gray-500 mb-4">
          Changing this updates their Firebase sign-in email directly — same account, same uid, no data is lost. A verification link is sent to the new address automatically.
        </p>
        <div className="flex gap-3">
          <input
            type="email"
            className="input-field flex-1"
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
          />
          <button
            onClick={handleSaveEmail}
            disabled={savingEmail || !emailInput.trim() || emailInput.trim() === user.email}
            className="px-4 py-2 bg-cerulean text-white rounded-lg font-semibold text-sm disabled:opacity-40 hover:bg-cerulean/90 transition-colors"
          >
            {savingEmail ? 'Saving…' : 'Update Email'}
          </button>
        </div>
      </div>

      {/* Profile fields */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Profile</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {PROFILE_FIELDS.map(({ key, label }) => (
            <div key={key}>
              <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
              <input
                type="text"
                className="input-field"
                value={fields[key] || ''}
                onChange={(e) => setFields((prev) => ({ ...prev, [key]: e.target.value }))}
              />
            </div>
          ))}
        </div>
        <div className="mt-4">
          <label className="block text-xs font-medium text-gray-500 mb-1">Bio</label>
          <textarea
            className="input-field"
            rows={3}
            value={bio}
            onChange={(e) => setBio(e.target.value)}
          />
        </div>
        <div className="mt-4 flex justify-end">
          <button
            onClick={handleSaveProfile}
            disabled={savingProfile}
            className="px-4 py-2 bg-cerulean text-white rounded-lg font-semibold text-sm disabled:opacity-40 hover:bg-cerulean/90 transition-colors"
          >
            {savingProfile ? 'Saving…' : 'Save Profile'}
          </button>
        </div>
      </div>

      {/* Families */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Families ({families.length})</h2>
        {families.length === 0 ? (
          <p className="text-sm text-gray-400">Not a member of any family.</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {families.map((f) => (
              <li key={f.id} className="py-2 flex items-center justify-between text-sm">
                <span className="text-gray-900 font-medium">{f.name || f.id}</span>
                <span className="text-gray-400 text-xs">{f.plan || 'free'} plan</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export default SuperAdminUserProfile;
